import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';

import DebounceInput from '~src/shared/form/inputs/DebounceInput';
import { ChipsWrapper } from '~src/shared/form/customChips/customChips.sc';
import SecondaryButton from '~src/shared/buttons/basicButtons/SecondaryButton.sc';
import PrimaryButton from '~src/shared/buttons/basicButtons/PrimaryButton.sc';
import { StoreI } from '~src/rootReducer';
import { OkrLabel, OkrLabelAdd } from '~src/store/okrs/interfaces';
import { ApiReduxResp } from '~src/services/apiMiddleware';
import LoadingSpinner from '~src/shared/loadingSpinner/LoadingSpinner';
import useDelayedLoading from '~src/services/hooks/useDelayedLoading';
import { isAdmin } from '~src/store/session/session.reducer';
import SvgIcon from '~src/shared/SvgIcon';
import useFocusFirstInput from '~src/services/hooks/useFocusFirstInput';
import { addLabel, deleteLabel, fetchLabels } from '~src/store/okrs/labels/actions';

import {
  ButtonSection,
  CreationHeader,
  DeleteIcon,
  FormTitle,
  LabelFormWrapper,
  LabelSearch,
  LabelSuggestionList,
  LabelsWrapper,
} from './labelSelectorForm.sc';
import LabelElement from './LabelElement';
import DeletionConfirmationModal from './DeletionConfirmationModal';

interface StateProps {
  loadedLabels: OkrLabel[];
  loadingCreation: boolean;
  loadingDelete: boolean;
  loading: boolean;
  isAdmin: boolean;
}

interface DispatchProps {
  fetchLabels: (name: string) => ApiReduxResp<OkrLabel[]>;
  deleteLabel: (id: string) => ApiReduxResp<null>;
  addLabel: (label: OkrLabelAdd) => ApiReduxResp<OkrLabel>;
}

interface OwnProps {
  isShown: boolean;
  labels: OkrLabel[];
  save: (labels: OkrLabel[]) => void;
  hide: () => void;
}

type Props = DispatchProps & StateProps & OwnProps;

const LabelSelectorForm: FC<Props> = props => {
  const save = () => {
    props.save(selectedLabels);
    props.hide();
  };

  const { setInputRef, inputRef } = useFocusFirstInput(props.isShown);

  const [selectedLabels, setSelectedLabels] = useState<OkrLabel[]>(props.labels);

  useEffect(() => {
    setSelectedLabels(props.labels);
  }, [props.labels]);

  const addToSelected = (item: OkrLabel) => {
    if (!selectedLabels.find(el => el.id === item.id)) {
      setSelectedLabels(l => [...l, item]);
      if (inputRef.current) {
        inputRef.current.value = '';
        inputRef.current.focus();
      }
    }
  };

  const onDelete = (item: OkrLabel) => {
    setSelectedLabels(l => l.filter(label => item.id !== label.id));
  };

  const [searchedLabel, setSearchedLabel] = useState('');

  const onSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchedLabel(e.target.value);
    if (e.target.value.length < 1) {
      return Promise.resolve();
    }
    return props.fetchLabels(e.target.value).then(() => Promise.resolve());
  };

  const delayedCreationLoading = useDelayedLoading(props.loadingCreation);
  const delayedListLoading = useDelayedLoading(props.loading);

  const clickAddLabel = () => {
    props
      .addLabel({
        name: searchedLabel,
      })
      .then(res => {
        addToSelected(res.response.data);
      });
  };

  const [labelToDelete, setLabelToDelete] = useState<OkrLabel | null>(null);

  const hideDeletion = () => setLabelToDelete(null);

  const deleteLabelTrigger = (id: string) => {
    props.deleteLabel(id).then(() => {
      hideDeletion();
    });
  };

  const cancel = () => {
    setSelectedLabels(props.labels);
    setSearchedLabel('');
    props.hide();
  };

  const addFirstNotSelected = () => {
    if (props.loadedLabels.length > 0) {
      const labelToAdd = props.loadedLabels.find(
        label => selectedLabels.findIndex(selectedLabel => selectedLabel.id === label.id) === -1,
      );
      if (labelToAdd) {
        addToSelected(labelToAdd);
      }
    }
  };

  const keyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    // when hitting enter prevent the Objective form to be submitted
    if (e.keyCode === 13) {
      e.preventDefault();
      // add the first of the searched labels OR create the searched one
      if (props.loadedLabels.length > 0) {
        addFirstNotSelected();
      } else {
        clickAddLabel();
      }
    }
    // hitting Backspace to remove labels
    if (e.keyCode === 8 && searchedLabel.length < 1) {
      setSelectedLabels(l => l.splice(0, l.length - 1));
    }
  };

  const [t] = useTranslation();

  return (
    <LabelFormWrapper>
      {labelToDelete && (
        <DeletionConfirmationModal
          hide={hideDeletion}
          delete={deleteLabelTrigger}
          label={labelToDelete}
          loading={props.loadingDelete}
        />
      )}
      <FormTitle data-translation="x.okr.labels.title">{t('x.okr.labels.title')}</FormTitle>
      <LabelSearch>
        <ChipsWrapper onKeyDown={keyDown}>
          {selectedLabels.map(label => (
            <LabelsWrapper $inList key={label.name}>
              <LabelElement label={label} add={onDelete} type="closed" />
            </LabelsWrapper>
          ))}
          <DebounceInput
            setInputNode={setInputRef}
            placeholder={{
              key: 'x.okr.labels.input.placeholder',
              value: t('x.okr.labels.input.placeholder'),
            }}
            onChange={onSearch}
            debounceTimeout={200}
          />
        </ChipsWrapper>
      </LabelSearch>
      <LabelSuggestionList>
        {delayedListLoading ? (
          <LoadingSpinner />
        ) : props.loadedLabels.length > 0 ? (
          props.loadedLabels.map(label => {
            const deleteThis = () => setLabelToDelete(label);
            const isSelected =
              selectedLabels.findIndex(selectedLabel => selectedLabel.id === label.id) !== -1;
            return (
              <LabelsWrapper key={label.id}>
                <LabelElement
                  key={label.id}
                  label={label}
                  type="add"
                  add={addToSelected}
                  isSelected={isSelected}
                />
                {props.isAdmin && !isSelected && (
                  <DeleteIcon onClick={deleteThis}>
                    <SvgIcon width={22} name="iconBin" />
                  </DeleteIcon>
                )}
              </LabelsWrapper>
            );
          })
        ) : (
          searchedLabel.length > 0 && (
            <>
              <CreationHeader data-translation="x.okr.labels.create.title">
                {t('x.okr.labels.create.title')}
              </CreationHeader>
              <LabelsWrapper>
                <LabelElement
                  loading={delayedCreationLoading}
                  create={clickAddLabel}
                  type="add"
                  name={searchedLabel}
                />
              </LabelsWrapper>
            </>
          )
        )}
      </LabelSuggestionList>
      <ButtonSection>
        <SecondaryButton type="button" onClick={cancel} data-translation="x.okr.labels.button.cancel">
          {t('x.okr.labels.button.cancel')}
        </SecondaryButton>
        <PrimaryButton onClick={save} type="button" data-translation="x.okr.labels.button.done">
          {t('x.okr.labels.button.done')}
        </PrimaryButton>
      </ButtonSection>
    </LabelFormWrapper>
  );
};

const mapStateToProps = (state: StoreI): StateProps => ({
  loadedLabels: state.okrs.labels.list,
  loadingCreation: state.okrs.labels.loadingCreation,
  loadingDelete: state.okrs.labels.loadingDelete,
  loading: state.okrs.labels.loadingList,
  isAdmin: isAdmin(state),
});

const mapDispatchToProps = (dispatch: any): DispatchProps => ({
  fetchLabels: name => dispatch(fetchLabels(name)),
  addLabel: label => dispatch(addLabel(label)),
  deleteLabel: id => dispatch(deleteLabel(id)),
});

const LabelSelectorFormConnected = connect(mapStateToProps, mapDispatchToProps)(LabelSelectorForm);

export default LabelSelectorFormConnected;
